import React, { useState, useEffect } from "react"
import { X, Package, Calendar, Hash, Plus, Loader, AlertTriangle } from "lucide-react"
import { useTheme } from "../contexts/ThemeContext"

const API_URL = process.env.REACT_APP_API_URL

/**
 * Modal de creación de albarán PENDIENTE para clientes en modo `mixto`.
 *
 * Lo abre admin/proveedor/cliente desde GestionPedidos cuando edita el campo
 * `albaran` y detecta que el cliente del pedido está en modo mixto. Cada línea
 * del pedido se muestra con su `cantidad_pedida` (en unidades del pedido) y
 * la unidad base; el creador rellena la cantidad real (en unidades base) y
 * opcionalmente lote + fecha de vencimiento.
 *
 * Al confirmar dispara POST /api/albaranes-pendientes. El albarán queda en
 * estado 'pendiente' y NO toca stock hasta que el cliente lo acepta.
 *
 * Props:
 *   - open
 *   - pedidoId
 *   - albaranNumero  ← texto que el user puso en el campo del pedido
 *   - onClose()
 *   - onCreated()    ← tras éxito (parent puede refrescar o avisar)
 */
export default function AlbaranMixtoCrearModal({ open, pedidoId, albaranNumero, onClose, onCreated }) {
  const { theme: themeCtx } = useTheme()
  const theme = {
    primary:      themeCtx?.primary      || "#3cbf9a",
    primaryDark:  themeCtx?.primaryDark  || "#2da080",
    primaryLight: themeCtx?.primaryLight || "rgba(60, 191, 154, 0.10)",
  }

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [lineas, setLineas] = useState([])
  const [notas, setNotas] = useState("")

  // Cargar líneas del pedido al abrir
  useEffect(() => {
    if (!open || !pedidoId) return
    let cancelled = false
    async function cargar() {
      setLoading(true); setError("")
      try {
        const token = localStorage.getItem("token")
        const r = await fetch(`${API_URL}/api/pedidos/${pedidoId}/lineas`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const data = await r.json()
        if (!r.ok) throw new Error(data.message || "Error al cargar líneas")
        if (cancelled) return

        // Para cada línea sugerimos cantidad_unidades = cantidad_pedida * factor_unidadbase.
        // El user puede ajustarla si recibió más/menos.
        const parsed = (data.lineas || data || []).map(l => {
          const factor = parseFactor(l.unidadbasica)
          const sugerida = (parseInt(l.cantidad) || 0) * factor
          return {
            lpedido_id: l.id,
            producto_id: l.idarticulo || l.producto_id,
            nombre: l.nombre || `Producto ${l.idarticulo}`,
            ref_proveedor: l.ref_proveedor || "",
            unidadbasica: l.unidadbasica || "",
            cantidad_pedida: l.cantidad,
            factor,
            cantidad_unidades: sugerida,    // editable
            lote: "",
            fecha_vencimiento: "",
            sin_caducidad: false,
          }
        })
        setLineas(parsed)
      } catch (e) {
        if (!cancelled) setError(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    cargar()
    return () => { cancelled = true }
  }, [open, pedidoId])

  if (!open) return null

  const handleLineaChange = (idx, field, value) => {
    setLineas(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l))
  }

  const handleConfirmar = async () => {
    setError("")
    // Al CREAR el albarán, lote y fecha son OPCIONALES: el admin/proveedor a
    // veces no los conoce porque vienen en la caja física. La validación dura
    // (lote obligatorio) se hace cuando el cliente acepta y verifica el
    // material recibido. Solo validamos que haya cantidad > 0 en al menos 1
    // línea para no crear albaranes vacíos.
    const validas = lineas.filter(l => parseInt(l.cantidad_unidades) > 0)
    if (validas.length === 0) {
      setError("Al menos una línea debe tener cantidad > 0.")
      return
    }

    setSaving(true)
    try {
      const token = localStorage.getItem("token")
      const body = {
        pedido_id: pedidoId,
        numero: albaranNumero,
        notas: notas || null,
        lineas: validas.map(l => ({
          lpedido_id: l.lpedido_id,
          producto_id: l.producto_id,
          cantidad_unidades: parseInt(l.cantidad_unidades),
          lote: l.lote ? l.lote.trim() : null,
          fecha_vencimiento: l.sin_caducidad ? null : (l.fecha_vencimiento || null),
        })),
      }
      const r = await fetch(`${API_URL}/api/albaranes-pendientes`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const data = await r.json()
      if (!r.ok) {
        if (r.status === 409) throw new Error(data.message)  // ya existe pendiente
        throw new Error(data.message || "Error al crear el albarán pendiente")
      }
      onCreated?.(data)
      onClose?.()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: theme.primaryLight }}>
          <div>
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Package size={18} style={{ color: theme.primaryDark }} />
              Cargar albarán mixto
            </h2>
            <p className="text-xs text-gray-600 mt-1">
              Pedido #{pedidoId} · Albarán <strong>{albaranNumero || "—"}</strong> ·
              Queda <strong>pendiente</strong> de aceptación por el cliente (no toca stock).
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/50" disabled={saving}>
            <X size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader size={20} className="animate-spin mr-2" /> Cargando líneas del pedido...
            </div>
          ) : (
            <>
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2">Producto</th>
                    <th className="text-left px-3 py-2">Unidad base</th>
                    <th className="text-right px-3 py-2">Cant. pedida</th>
                    <th className="text-right px-3 py-2">Cantidad recibida (uds.)</th>
                    <th className="text-left px-3 py-2">Lote</th>
                    <th className="text-left px-3 py-2">Vencimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((l, idx) => (
                    <tr key={l.lpedido_id} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-800">{l.nombre}</div>
                        {l.ref_proveedor && <div className="text-[10px] text-gray-500">{l.ref_proveedor}</div>}
                      </td>
                      <td className="px-3 py-2 text-gray-500 text-[11px]">{l.unidadbasica || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <div>{l.cantidad_pedida}</div>
                        <div className="text-[10px] text-gray-400">× {l.factor} = {l.cantidad_pedida * l.factor} ud.</div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number" min="0"
                          value={l.cantidad_unidades}
                          onChange={(e) => handleLineaChange(idx, "cantidad_unidades", e.target.value)}
                          className="w-24 px-2 py-1 text-right border-2 border-gray-200 rounded focus:outline-none transition-colors"
                          onFocus={(e) => e.target.style.borderColor = theme.primary}
                          onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <Hash size={11} className="text-gray-400" />
                          <input
                            type="text"
                            value={l.lote}
                            onChange={(e) => handleLineaChange(idx, "lote", e.target.value.toUpperCase())}
                            placeholder="Opcional"
                            className="w-28 px-2 py-1 border-2 border-gray-200 rounded text-[11px] uppercase focus:outline-none"
                            onFocus={(e) => e.target.style.borderColor = theme.primary}
                            onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                          />
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <Calendar size={11} className="text-gray-400" />
                          <input
                            type="date"
                            value={l.fecha_vencimiento}
                            onChange={(e) => handleLineaChange(idx, "fecha_vencimiento", e.target.value)}
                            disabled={l.sin_caducidad}
                            className="w-32 px-2 py-1 border-2 border-gray-200 rounded text-[11px] focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                          />
                          <label className="text-[10px] text-gray-500 flex items-center gap-1 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={l.sin_caducidad}
                              onChange={(e) => handleLineaChange(idx, "sin_caducidad", e.target.checked)}
                              className="w-3 h-3"
                            />
                            Sin caducidad
                          </label>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Notas (opcional)</label>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  rows={2}
                  placeholder="Notas internas para el cliente al revisar..."
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded text-sm focus:outline-none"
                  onFocus={(e) => e.target.style.borderColor = theme.primary}
                  onBlur={(e) => e.target.style.borderColor = "#e5e7eb"}
                />
              </div>

              {error && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirmar}
            disabled={saving || loading || lineas.length === 0}
            className="px-4 py-2 text-sm font-semibold text-white rounded flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: theme.primary }}
          >
            {saving ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? "Guardando..." : "Crear albarán pendiente"}
          </button>
        </div>
      </div>
    </div>
  )
}

// Lo mismo que parseUnidadBaseFactor en backend: del último número en la cadena
// "1 caja de 100" → 100. Si no encuentra 2+ números → 1.
function parseFactor(unidadbasica) {
  if (!unidadbasica) return 1
  const matches = String(unidadbasica).match(/\d+/g)
  if (!matches || matches.length < 2) return 1
  const n = parseInt(matches[matches.length - 1], 10)
  return (isNaN(n) || n < 1) ? 1 : n
}
